import type { Disposable } from "@/shared/contracts/lifecycle";
import { getMessages } from "@/i18n/runtime";
import type {
  HoverIdleSnapshot,
  HovCellGainUpgradeIndex,
  HovGainUpgradeMpTarget,
} from "../types";
import { UpgradeInteractionController } from "./upgradeInteractionController";
import {
  createUpgradeTooltip,
  type UpgradeTooltipOptions,
} from "./upgradeTooltipView";
import type { HovGainUpgradeTooltipViewModel } from "./hovGainUpgradeTooltipViewModel";
import type { HoverPointerModel } from "../input/hoverPointerModel";
import type { HoverIdleMotionPolicy } from "../core/hoverIdleMotionPolicy";
import {
  DEFAULT_UPGRADE_REPEAT_INITIAL_INTERVAL_MS,
  type UpgradeRepeatInitialIntervalSource,
  type UpgradeRepeatSpeedMultiplierSource,
} from "./upgradeRepeatTiming";
import { applyHovThemeHue } from "../nHov/nHovThemeCss";
import { MOUSE_POINTER_ICON_SVG_SOURCE } from "@/shared/ui/mousePointerIcon";
import {
  isMpmHovGainUpgradesActive,
  computeMpmAvailableMpFromOccupancy,
} from "@/features/hoverIdle/mpm/mpmCapacity";
import { resolveDirectMpTargetViewState } from "@/features/hoverIdle/mpm/directMpTargetViewState";
import { syncDirectMpSlot } from "@/features/hoverIdle/mpm/directMpSlotView";
import { MpSlotChargeController } from "@/features/hoverIdle/mpm/mpSlotChargeController";
import { toHovGainUpgradeMpTargetKey } from "@/features/hoverIdle/mpm/mpmHovGainUpgradeTargets";
import banIconSource from "bootstrap-icons/icons/ban.svg?raw";
import pauseIconSource from "bootstrap-icons/icons/pause-fill.svg?raw";

export interface GroupingHovGainUpgradeRowState {
  readonly costText: string;
  readonly isAffordable: boolean;
  readonly isVisible: boolean;
  /**
   * True only when the next purchase reaches an upgrade milestone AND it is
   * currently affordable. Drives the subtle `data-milestone-ready` affordance.
   */
  readonly isMilestoneReady: boolean;
  readonly tooltip: HovGainUpgradeTooltipViewModel;
}

interface GroupingHovGainUpgradeEntryBase {
  readonly id: string;
  readonly label: string;
  readonly cardAriaLabel: string;
  readonly tooltip: UpgradeTooltipOptions;
  readonly render: (
    snapshot: HoverIdleSnapshot,
  ) => GroupingHovGainUpgradeRowState;
}

export interface GlobalHovGainUpgradeEntry extends GroupingHovGainUpgradeEntryBase {
  readonly kind: "global";
}

export interface HovCellGainUpgradeEntry extends GroupingHovGainUpgradeEntryBase {
  readonly kind: "cell";
  readonly index: HovCellGainUpgradeIndex;
}

export type GroupingHovGainUpgradeEntry =
  | GlobalHovGainUpgradeEntry
  | HovCellGainUpgradeEntry;

export interface GroupingHovGainUpgradePurchaseResult {
  readonly ok: boolean;
  readonly isMilestone?: boolean;
}

export type GroupingHovGainUpgradePurchaseOutcome =
  | boolean
  | GroupingHovGainUpgradePurchaseResult;

export interface GroupingHovGainUpgradePurchaseCallbacks {
  readonly onPurchaseGlobalHovGainUpgrade: () => GroupingHovGainUpgradePurchaseOutcome;
  readonly onPurchaseHovCellGainUpgrade: (
    index: HovCellGainUpgradeIndex,
  ) => GroupingHovGainUpgradePurchaseOutcome;
}

export interface GroupingHovGainUpgradePanelViewOptions extends GroupingHovGainUpgradePurchaseCallbacks {
  readonly pointerModel: HoverPointerModel;
  readonly entries: readonly GroupingHovGainUpgradeEntry[];
  readonly isHngDrawerActive?: () => boolean;
  readonly initialIntervalMs?: UpgradeRepeatInitialIntervalSource;
  readonly speedMultiplier?: UpgradeRepeatSpeedMultiplierSource;
  readonly getMotionPolicy?: () => HoverIdleMotionPolicy;
  readonly getThemeHue?: (snapshot: HoverIdleSnapshot) => number | null;
  readonly onHovGainUpgradeMpAssign?: (target: HovGainUpgradeMpTarget) => void;
  readonly onHovGainUpgradeMpRemove?: (target: HovGainUpgradeMpTarget) => void;
  readonly onMpWallAssign?: (target: HovGainUpgradeMpTarget) => void;
  readonly onMpWallRemove?: (target: HovGainUpgradeMpTarget) => void;
  readonly getHoverToggleDurationMs?: () => number;
  readonly onPurchaseBurstStart?: () => void;
  readonly onPurchaseBurstEnd?: () => void;
}

export function purchaseGroupingHovGainUpgradeEntry(
  entry: GroupingHovGainUpgradeEntry,
  callbacks: GroupingHovGainUpgradePurchaseCallbacks,
): GroupingHovGainUpgradePurchaseResult {
  const outcome =
    entry.kind === "global"
      ? callbacks.onPurchaseGlobalHovGainUpgrade()
      : callbacks.onPurchaseHovCellGainUpgrade(entry.index);
  return normalizeGroupingHovGainUpgradePurchaseOutcome(outcome);
}

function normalizeGroupingHovGainUpgradePurchaseOutcome(
  outcome: GroupingHovGainUpgradePurchaseOutcome,
): GroupingHovGainUpgradePurchaseResult {
  if (typeof outcome === "boolean") {
    return { ok: outcome };
  }
  return outcome;
}

function isGroupingHovGainUpgradePurchaseMilestone(
  result: GroupingHovGainUpgradePurchaseResult,
  wasMilestoneReady: boolean,
): boolean {
  return result.isMilestone ?? wasMilestoneReady;
}

function mergePendingPulseRequest(
  current: PendingPulseRequest | undefined,
  next: PendingPulseRequest,
): PendingPulseRequest {
  if (next.kind === "milestone") {
    return next;
  }
  if (current?.kind === "milestone") {
    return current;
  }
  return next;
}

interface NormalPulseRequest {
  readonly kind: "normal";
  readonly pulseSeconds: number;
}

interface MilestonePulseRequest {
  readonly kind: "milestone";
}

interface ClearPulseRequest {
  readonly kind: "clear";
}

type PendingPulseRequest =
  | NormalPulseRequest
  | MilestonePulseRequest
  | ClearPulseRequest;

export function shouldPulseGroupingHovGainUpgradePurchase(
  purchaseSucceeded: boolean,
  motionPolicy: HoverIdleMotionPolicy,
): boolean {
  return purchaseSucceeded && motionPolicy.upgradePurchasePulse;
}

/**
 * Decides whether a successful purchase should fire the dedicated milestone
 * pulse instead of the normal purchase pulse. `wasMilestoneReady` can come from
 * the app-level purchase result; boolean-only callbacks fall back to the row's
 * pre-purchase milestone-ready state.
 */
export function shouldMilestonePulseGroupingHovGainUpgradePurchase(
  purchaseSucceeded: boolean,
  wasMilestoneReady: boolean,
  motionPolicy: HoverIdleMotionPolicy,
): boolean {
  return (
    purchaseSucceeded && wasMilestoneReady && motionPolicy.upgradePurchasePulse
  );
}

/** Fixed duration of the milestone purchase pulse; mirrors the CSS animation. */
const MILESTONE_PULSE_DURATION_MS = 1000;

interface HovGainMpSlotNodes {
  readonly element: HTMLDivElement;
  readonly controller: MpSlotChargeController;
  readonly target: HovGainUpgradeMpTarget;
}

interface HngDrawerToggleNodes {
  readonly button: HTMLButtonElement;
  readonly caret: HTMLSpanElement;
  readonly label: HTMLSpanElement;
}

interface GroupingHovGainUpgradeRowNodes {
  readonly entry: GroupingHovGainUpgradeEntry;
  readonly wrapper: HTMLDivElement;
  readonly button: HTMLButtonElement;
  readonly costLine: HTMLSpanElement;
  readonly card: HTMLDivElement;
  readonly cardLabel: HTMLSpanElement;
  readonly tooltipRoot: HTMLDivElement;
  readonly tooltipTitle: HTMLDivElement;
  readonly tooltipRegion: HTMLSpanElement;
  readonly tooltipSummary: HTMLParagraphElement;
  readonly tooltipEffectRow: HTMLDivElement;
  readonly tooltipEffectLabel: HTMLSpanElement;
  readonly tooltipEffectValue: HTMLSpanElement;
  readonly tooltipAfterRow: HTMLDivElement;
  readonly tooltipAfterLabel: HTMLSpanElement;
  readonly tooltipAfterValue: HTMLSpanElement;
  readonly tooltipCostRow: HTMLDivElement;
  readonly tooltipCostLabel: HTMLSpanElement;
  readonly tooltipCostValue: HTMLSpanElement;
  readonly tooltipStateRow: HTMLDivElement;
  readonly tooltipStateLabel: HTMLSpanElement;
  readonly tooltipStateValue: HTMLSpanElement;
  readonly interaction: UpgradeInteractionController;
  readonly mpSlot: HovGainMpSlotNodes | null;
  readonly wallMpSlot: HovGainMpSlotNodes | null;
  tooltipViewModel: HovGainUpgradeTooltipViewModel;
  isAffordable: boolean;
  /**
   * Mirrors {@link GroupingHovGainUpgradeRowState.isMilestoneReady}. Read at
   * purchase time to decide between the normal and milestone purchase pulse.
   */
  isMilestoneReady: boolean;
  isMpOperative: boolean;
}

const DEFAULT_MOTION_POLICY: HoverIdleMotionPolicy = {
  hovCellHoverPulse: true,
  upgradePurchasePulse: true,
  mHovAnimationSpeed: "normal",
  mHovChargeDurationMs: 0,
  nHovAnimationSpeed: "normal",
  nHovChargeDurationMs: 0,
  oHovChargeDurationMs: 0,
  outsideResetBurstAnimation: "full",
};

function createEmptyTooltipViewModel(): HovGainUpgradeTooltipViewModel {
  return {
    title: "",
    region: "",
    summary: "",
    effectLabel: "",
    effect: "",
    afterLabel: "",
    after: "",
    costLabel: "",
    cost: "",
    stateLabel: "",
    state: "",
  };
}

export class GroupingHovGainUpgradePanelView implements Disposable {
  readonly root: HTMLDivElement;
  private readonly rows: readonly GroupingHovGainUpgradeRowNodes[];
  private readonly purchaseCallbacks: GroupingHovGainUpgradePurchaseCallbacks;
  private readonly hngDrawerToggle: HngDrawerToggleNodes | null;
  private readonly isHngDrawerActive: () => boolean;
  private readonly initialIntervalMs?: UpgradeRepeatInitialIntervalSource;
  private readonly speedMultiplier?: UpgradeRepeatSpeedMultiplierSource;
  private readonly getMotionPolicy: () => HoverIdleMotionPolicy;
  private readonly getThemeHue:
    | ((snapshot: HoverIdleSnapshot) => number | null)
    | null;
  private readonly pointerModel: HoverPointerModel;
  private readonly onHovGainUpgradeMpAssign:
    | ((target: HovGainUpgradeMpTarget) => void)
    | null;
  private readonly onHovGainUpgradeMpRemove:
    | ((target: HovGainUpgradeMpTarget) => void)
    | null;
  private readonly onMpWallAssign:
    | ((target: HovGainUpgradeMpTarget) => void)
    | null;
  private readonly onMpWallRemove:
    | ((target: HovGainUpgradeMpTarget) => void)
    | null;
  private readonly getHoverToggleDurationMs: (() => number) | null;
  private readonly onPurchaseBurstStart: (() => void) | null;
  private readonly onPurchaseBurstEnd: (() => void) | null;
  private readonly pulseClearTimeouts = new Map<HTMLButtonElement, number>();
  private readonly milestonePulseClearTimeouts = new Map<
    HTMLButtonElement,
    number
  >();
  private readonly pendingPulseRequests = new Map<
    HTMLButtonElement,
    PendingPulseRequest
  >();
  private pulseBurstDepth = 0;
  private isInputLocked = false;
  private isHngDrawerExpanded = false;
  private lastSnapshot: HoverIdleSnapshot | null = null;

  private readonly handleHngDrawerToggleClick = (): void => {
    if (!this.isHngDrawerActive()) {
      return;
    }
    this.isHngDrawerExpanded = !this.isHngDrawerExpanded;
    if (this.lastSnapshot !== null) {
      this.update(this.lastSnapshot);
    }
  };

  constructor(options: GroupingHovGainUpgradePanelViewOptions) {
    this.root = document.createElement("div");
    this.root.className = "hover-idle-grouping-hov-gain-upgrade-panel";
    this.isHngDrawerActive = options.isHngDrawerActive ?? (() => false);
    this.hngDrawerToggle =
      options.isHngDrawerActive === undefined
        ? null
        : this.createHngDrawerToggle();
    if (this.hngDrawerToggle !== null) {
      this.root.append(this.hngDrawerToggle.button);
    }
    this.purchaseCallbacks = {
      onPurchaseGlobalHovGainUpgrade: options.onPurchaseGlobalHovGainUpgrade,
      onPurchaseHovCellGainUpgrade: options.onPurchaseHovCellGainUpgrade,
    };
    this.initialIntervalMs = options.initialIntervalMs;
    this.speedMultiplier = options.speedMultiplier;
    this.getMotionPolicy =
      options.getMotionPolicy ??
      ((): HoverIdleMotionPolicy => DEFAULT_MOTION_POLICY);
    this.getThemeHue = options.getThemeHue ?? null;
    this.pointerModel = options.pointerModel;
    this.onHovGainUpgradeMpAssign = options.onHovGainUpgradeMpAssign ?? null;
    this.onHovGainUpgradeMpRemove = options.onHovGainUpgradeMpRemove ?? null;
    this.onMpWallAssign = options.onMpWallAssign ?? null;
    this.onMpWallRemove = options.onMpWallRemove ?? null;
    this.getHoverToggleDurationMs = options.getHoverToggleDurationMs ?? null;
    this.onPurchaseBurstStart = options.onPurchaseBurstStart ?? null;
    this.onPurchaseBurstEnd = options.onPurchaseBurstEnd ?? null;

    const rows: GroupingHovGainUpgradeRowNodes[] = [];
    for (const entry of options.entries) {
      rows.push(this.createRow(entry, options.pointerModel));
    }
    this.rows = rows;
  }

  update(snapshot: HoverIdleSnapshot): void {
    this.lastSnapshot = snapshot;
    applyHovThemeHue(this.root, this.getThemeHue?.(snapshot) ?? null);
    const mpGateOpen = isMpmHovGainUpgradesActive(snapshot.mpm);
    const availableMp = mpGateOpen
      ? computeMpmAvailableMpFromOccupancy({
          mpCapacity: snapshot.mpCapacity,
          state: snapshot.mpm,
        })
      : 0;

    let anyVisible = false;
    let anyHngVisible = false;
    const hngDrawerActive = this.isHngDrawerActive();
    for (const row of this.rows) {
      const state = row.entry.render(snapshot);
      const isHngRow = row.entry.kind === "cell";
      const isHiddenByHngDrawer =
        hngDrawerActive && !this.isHngDrawerExpanded && isHngRow;
      row.wrapper.hidden = !state.isVisible || isHiddenByHngDrawer;
      row.wrapper.dataset.hngDrawerHidden = isHiddenByHngDrawer
        ? "true"
        : "false";
      if (isHngRow && state.isVisible) {
        anyHngVisible = true;
      }
      if (!state.isVisible) {
        row.isAffordable = false;
        row.interaction.hideTooltip();
        row.interaction.setInputLocked(true);
        row.button.dataset.blocked = "false";
        row.button.dataset.mpAssigned = "false";
        row.button.dataset.mpOperative = "false";
        row.button.dataset.mpBlocked = "false";
        delete row.button.dataset.mpBlockedReason;
        this.clearAllPulses(row.button);
        this.setMilestoneReady(row, false);
        for (const slot of [row.mpSlot, row.wallMpSlot]) {
          if (slot === null) continue;
          slot.controller.setInputLocked(true);
          syncDirectMpSlot(slot, null);
        }
        continue;
      }
      if (!isHiddenByHngDrawer) {
        anyVisible = true;
      }

      const affordable = state.isAffordable ? "true" : "false";
      row.isAffordable = state.isAffordable;
      row.costLine.textContent = state.costText;
      row.cardLabel.textContent = row.entry.label;
      row.button.setAttribute("aria-disabled", String(!state.isAffordable));
      row.button.dataset.affordable = affordable;
      row.card.dataset.affordable = affordable;
      row.tooltipRoot.dataset.affordable = affordable;
      this.setMilestoneReady(row, state.isMilestoneReady);
      row.tooltipViewModel = state.tooltip;
      if (row.interaction.isTooltipShown()) {
        this.applyTooltipViewModel(row, state.tooltip);
      }
      row.button.dataset.blocked = "false";
      row.button.dataset.mpBlocked = "false";
      delete row.button.dataset.mpBlockedReason;

      const mpSlot = row.mpSlot;
      let nextMpAssigned = false;
      let nextMpOperative = false;
      let blockManualPointer = false;
      if (mpSlot !== null) {
        const assignments = snapshot.mpm.hovGainUpgrades.assignments;
        const targetKey = toHovGainUpgradeMpTargetKey(mpSlot.target);
        const isFilled =
          targetKey !== null &&
          assignments.some((a) => toHovGainUpgradeMpTargetKey(a) === targetKey);
        const directMpState = resolveDirectMpTargetViewState({
          assigned: isFilled,
          targetActive: !isHiddenByHngDrawer,
          sectionEnabled: mpGateOpen,
          canAssign: availableMp > 0,
          blockManualPointerWhenAssigned: true,
          directMpBlockedByActiveWall:
            snapshot.hovGainUpgradeDirectMpBlockedByActiveWall,
          directMpBlockedReason: snapshot.hovGainUpgradeDirectMpBlockedReason,
        });
        nextMpAssigned = directMpState.assigned;
        nextMpOperative = directMpState.operative;
        blockManualPointer = directMpState.blockManualPointer;
        row.button.dataset.mpBlocked = directMpState.mpBlocked
          ? "true"
          : "false";
        if (directMpState.mpBlockedReason !== undefined) {
          row.button.dataset.mpBlockedReason = directMpState.mpBlockedReason;
        } else {
          delete row.button.dataset.mpBlockedReason;
        }
        row.button.dataset.blocked = directMpState.mpBlocked ? "true" : "false";
        syncDirectMpSlot(mpSlot, directMpState);
      }
      if (row.wallMpSlot !== null) {
        const assignments = snapshot.wall.mpWallAssignments;
        row.wrapper.dataset.wallMpActive = String(assignments != null);
        const selected =
          assignments?.kind === "hov-gain-upgrade" ? assignments.target : null;
        const wallMpState =
          assignments == null
            ? null
            : resolveDirectMpTargetViewState({
                assigned:
                  selected != null &&
                  toHovGainUpgradeMpTargetKey(selected) ===
                    toHovGainUpgradeMpTargetKey(row.wallMpSlot.target),
                targetActive: !isHiddenByHngDrawer,
                canAssign: true,
                blockManualPointerWhenAssigned: true,
              });
        syncDirectMpSlot(row.wallMpSlot, wallMpState);
        if (wallMpState?.operative) {
          nextMpAssigned = true;
          nextMpOperative = true;
          blockManualPointer = wallMpState.blockManualPointer;
          row.button.dataset.mpBlocked = "false";
          row.button.dataset.blocked = "false";
          delete row.button.dataset.mpBlockedReason;
        }
      }
      row.button.dataset.mpAssigned = nextMpAssigned ? "true" : "false";
      row.button.dataset.mpOperative = nextMpOperative ? "true" : "false";
      row.isMpOperative = nextMpOperative;
      row.interaction.setInputLocked(
        this.isInputLocked || blockManualPointer || isHiddenByHngDrawer,
      );
      row.mpSlot?.controller.setInputLocked(
        this.isInputLocked || isHiddenByHngDrawer,
      );
      row.wallMpSlot?.controller.setInputLocked(
        this.isInputLocked || isHiddenByHngDrawer,
      );
      if (isHiddenByHngDrawer) {
        row.interaction.hideTooltip();
        row.mpSlot?.controller.clearPointerHover();
        row.wallMpSlot?.controller.clearPointerHover();
        this.clearAllPulses(row.button);
      } else if (nextMpOperative) {
        this.clearAllPulses(row.button);
      }
    }
    this.syncHngDrawerToggle(hngDrawerActive, anyHngVisible);
    this.root.hidden = !anyVisible;
    if (!anyVisible) {
      this.hideAllTooltips();
    }
  }

  dispose(): void {
    this.hideAllTooltips();
    this.pendingPulseRequests.clear();
    this.hngDrawerToggle?.button.removeEventListener(
      "click",
      this.handleHngDrawerToggleClick,
    );
    for (const row of this.rows) {
      this.clearAllPulses(row.button);
      row.interaction.dispose();
      row.mpSlot?.controller.dispose();
      row.wallMpSlot?.controller.dispose();
    }
    this.root.remove();
  }

  setInputLocked(locked: boolean): void {
    if (locked === this.isInputLocked) {
      return;
    }
    this.isInputLocked = locked;
    this.root.dataset.inputLocked = locked ? "true" : "false";
    for (const row of this.rows) {
      row.interaction.setInputLocked(
        locked || row.isMpOperative || row.wrapper.hidden,
      );
      if (locked) {
        this.clearAllPulses(row.button);
      }
      row.mpSlot?.controller.setInputLocked(locked || row.wrapper.hidden);
      row.wallMpSlot?.controller.setInputLocked(locked || row.wrapper.hidden);
    }
  }

  private createHngDrawerToggle(): HngDrawerToggleNodes {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "hover-idle-hng-drawer-toggle";
    button.dataset.hngDrawerToggle = "true";
    button.hidden = true;
    button.addEventListener("click", this.handleHngDrawerToggleClick);

    const caret = document.createElement("span");
    caret.className = "hover-idle-hng-drawer-toggle-caret";
    caret.setAttribute("aria-hidden", "true");

    const label = document.createElement("span");
    label.className = "hover-idle-hng-drawer-toggle-label";

    button.append(caret, label);
    return { button, caret, label };
  }

  private syncHngDrawerToggle(active: boolean, hasHngRows: boolean): void {
    const toggle = this.hngDrawerToggle;
    if (toggle === null) {
      return;
    }
    const isAvailable = active && hasHngRows;
    const expanded = this.isHngDrawerExpanded;
    const messages = getMessages().hovView.hngDrawer;

    this.root.dataset.hngDrawerActive = isAvailable ? "true" : "false";
    this.root.dataset.hngDrawerExpanded = expanded ? "true" : "false";
    toggle.button.hidden = !isAvailable;
    toggle.button.dataset.expanded = expanded ? "true" : "false";
    toggle.button.setAttribute("aria-hidden", isAvailable ? "false" : "true");
    toggle.button.setAttribute("aria-expanded", expanded ? "true" : "false");
    toggle.button.setAttribute(
      "aria-label",
      expanded ? messages.collapseAriaLabel : messages.expandAriaLabel,
    );
    toggle.caret.textContent = expanded ? "\u2304" : "\u2303";
    toggle.label.hidden = expanded;
    toggle.label.textContent = expanded ? "" : messages.label;
  }

  private createRow(
    entry: GroupingHovGainUpgradeEntry,
    pointerModel: HoverPointerModel,
  ): GroupingHovGainUpgradeRowNodes {
    const dataCellIndex =
      entry.kind === "cell" ? String(entry.index - 1) : null;
    const wrapper = document.createElement("div");
    wrapper.className = "hover-idle-grouping-hov-gain-upgrade-row";
    wrapper.dataset.catalogEntryId = entry.id;
    if (dataCellIndex !== null) {
      wrapper.dataset.cellIndex = dataCellIndex;
    }

    const button = document.createElement("button");
    button.type = "button";
    button.className = "hover-idle-grouping-hov-gain-upgrade";
    button.dataset.kind = entry.kind;
    if (dataCellIndex !== null) {
      button.dataset.cellIndex = dataCellIndex;
    }
    button.dataset.virtualPointer = "allow";
    button.dataset.mpAssigned = "false";
    button.dataset.mpOperative = "false";
    button.dataset.mpBlocked = "false";
    button.dataset.blocked = "false";
    button.setAttribute("aria-describedby", entry.tooltip.id);
    const costLine = document.createElement("span");
    costLine.className = "hover-idle-grouping-hov-gain-upgrade-cost";
    button.append(costLine);

    const card = document.createElement("div");
    card.className =
      "hover-idle-upgrade-card hover-idle-grouping-hov-gain-upgrade-card";
    card.dataset.kind = entry.kind;
    if (dataCellIndex !== null) {
      card.dataset.cellIndex = dataCellIndex;
    }
    card.tabIndex = 0;
    card.setAttribute("aria-label", entry.cardAriaLabel);
    card.setAttribute("aria-describedby", entry.tooltip.id);
    const cardLabel = document.createElement("span");
    cardLabel.className = "hover-idle-grouping-hov-gain-upgrade-label";
    card.append(cardLabel);

    const mpSlot =
      this.onHovGainUpgradeMpAssign !== null &&
      this.onHovGainUpgradeMpRemove !== null
        ? this.createMpSlot(
            entry.kind === "global"
              ? "global"
              : { kind: "cell", index: entry.index },
          )
        : null;
    if (mpSlot !== null) {
      button.append(mpSlot.element);
    }

    const wallMpSlot =
      this.onMpWallAssign !== null && this.onMpWallRemove !== null
        ? this.createMpSlot(
            entry.kind === "global"
              ? "global"
              : { kind: "cell", index: entry.index },
            "wall",
          )
        : null;

    const tooltip = createUpgradeTooltip(entry.tooltip);
    tooltip.root.classList.add("hover-idle-grouping-hov-gain-upgrade-tooltip");
    tooltip.root.dataset.kind = entry.kind;
    if (dataCellIndex !== null) {
      tooltip.root.dataset.cellIndex = dataCellIndex;
    }

    let isAffordable = false;
    let isMilestoneReady = false;
    let interactionRef: UpgradeInteractionController | null = null;
    let rowNodesRef: GroupingHovGainUpgradeRowNodes | null = null;
    const interaction = new UpgradeInteractionController({
      button,
      card,
      tooltip: tooltip.root,
      tooltipAnchor: card,
      pointerModel,
      initialIntervalMs: this.initialIntervalMs,
      speedMultiplier: this.speedMultiplier,
      onUpgrade: ({ nextIntervalMs }) => {
        if (this.isInputLocked) {
          return false;
        }
        if (!isAffordable) {
          return false;
        }
        const wasMilestoneReady = isMilestoneReady;
        const purchaseResult = purchaseGroupingHovGainUpgradeEntry(
          entry,
          this.purchaseCallbacks,
        );
        const purchaseSucceeded = purchaseResult.ok;
        const motionPolicy = this.getMotionPolicy();
        if (
          shouldMilestonePulseGroupingHovGainUpgradePurchase(
            purchaseSucceeded,
            isGroupingHovGainUpgradePurchaseMilestone(
              purchaseResult,
              wasMilestoneReady,
            ),
            motionPolicy,
          )
        ) {
          this.queuePulseRequest(button, { kind: "milestone" });
        } else if (
          shouldPulseGroupingHovGainUpgradePurchase(
            purchaseSucceeded,
            motionPolicy,
          )
        ) {
          this.queuePulseRequest(button, {
            kind: "normal",
            pulseSeconds: nextIntervalMs / 1000,
          });
        } else if (purchaseSucceeded) {
          this.queuePulseRequest(button, { kind: "clear" });
        }
        if (purchaseSucceeded) {
          interactionRef?.hideTooltip();
        }
        return purchaseSucceeded;
      },
      onTooltipShow: () => {
        if (rowNodesRef !== null) {
          this.applyTooltipViewModel(rowNodesRef, rowNodesRef.tooltipViewModel);
        }
      },
      onUpgradeBurstStart: () => {
        this.beginPulseBurst();
        this.onPurchaseBurstStart?.();
      },
      onUpgradeBurstEnd: () => {
        this.onPurchaseBurstEnd?.();
        this.endPulseBurst();
      },
    });
    interactionRef = interaction;

    wrapper.append(button, card, tooltip.root);
    if (wallMpSlot !== null) wrapper.append(wallMpSlot.element);
    this.root.append(wrapper);

    const rowNodes: GroupingHovGainUpgradeRowNodes = {
      entry,
      wrapper,
      button,
      costLine,
      card,
      cardLabel,
      tooltipRoot: tooltip.root,
      tooltipTitle: tooltip.title,
      tooltipRegion: tooltip.region,
      tooltipSummary: tooltip.summary,
      tooltipEffectRow: tooltip.effectRow,
      tooltipEffectLabel: tooltip.effectLabel,
      tooltipEffectValue: tooltip.effectValue,
      tooltipAfterRow: tooltip.afterRow,
      tooltipAfterLabel: tooltip.afterLabel,
      tooltipAfterValue: tooltip.afterValue,
      tooltipCostRow: tooltip.costRow,
      tooltipCostLabel: tooltip.costLabel,
      tooltipCostValue: tooltip.costValue,
      tooltipStateRow: tooltip.stateRow,
      tooltipStateLabel: tooltip.stateLabel,
      tooltipStateValue: tooltip.stateValue,
      interaction,
      mpSlot,
      wallMpSlot,
      tooltipViewModel: createEmptyTooltipViewModel(),
      get isAffordable(): boolean {
        return isAffordable;
      },
      set isAffordable(next: boolean) {
        isAffordable = next;
      },
      get isMilestoneReady(): boolean {
        return isMilestoneReady;
      },
      set isMilestoneReady(next: boolean) {
        isMilestoneReady = next;
      },
      isMpOperative: false,
    };
    rowNodesRef = rowNodes;
    return rowNodes;
  }

  private applyTooltipViewModel(
    row: GroupingHovGainUpgradeRowNodes,
    tooltip: HovGainUpgradeTooltipViewModel,
  ): void {
    const isStateOnly = tooltip.displayMode === "state-only";
    row.tooltipTitle.textContent = tooltip.title;
    row.tooltipRegion.textContent = tooltip.region;
    row.tooltipSummary.textContent = tooltip.summary;
    row.tooltipEffectRow.hidden = isStateOnly;
    row.tooltipAfterRow.hidden = isStateOnly;
    row.tooltipCostRow.hidden = isStateOnly;
    row.tooltipStateRow.hidden = false;
    row.tooltipRoot.dataset.displayMode = isStateOnly ? "state-only" : "full";
    row.tooltipEffectLabel.textContent = tooltip.effectLabel;
    row.tooltipEffectValue.textContent = tooltip.effect;
    row.tooltipAfterLabel.textContent = tooltip.afterLabel;
    row.tooltipAfterValue.textContent = tooltip.after;
    row.tooltipCostLabel.textContent = tooltip.costLabel;
    row.tooltipCostValue.textContent = tooltip.cost;
    row.tooltipStateLabel.textContent = tooltip.stateLabel;
    row.tooltipStateValue.textContent = tooltip.state;
  }

  private createMpSlot(
    target: HovGainUpgradeMpTarget,
    scope: "mpm" | "wall" = "mpm",
  ): HovGainMpSlotNodes {
    const element = document.createElement("div");
    element.className = `hover-idle-mp-slot ${scope === "wall" ? "hover-idle-mp-slot--wall-corner" : "hover-idle-mp-slot--target-corner"}`;
    element.dataset.mpScope = scope;
    element.dataset.virtualPointer = "allow";
    element.dataset.mpAssigned = "false";
    element.dataset.mpOperative = "false";
    element.dataset.mpBlocked = "false";
    element.dataset.paused = "false";
    element.dataset.blocked = "false";
    element.hidden = true;
    if (scope === "wall") {
      element.tabIndex = 0;
      element.setAttribute("role", "button");
      element.setAttribute(
        "aria-label",
        getMessages().hoverTarget.wallMpSlotAria(
          target === "global" ? "HG" : `H${target.index}G`,
        ),
      );
    } else {
      element.setAttribute("aria-hidden", "true");
    }

    const fill = document.createElement("span");
    fill.className = "hover-idle-mp-slot-fill";
    fill.setAttribute("aria-hidden", "true");

    const icon = document.createElement("span");
    icon.className = "hover-idle-mp-slot-icon";
    icon.setAttribute("aria-hidden", "true");
    icon.innerHTML = MOUSE_POINTER_ICON_SVG_SOURCE;

    const pauseOverlay = document.createElement("span");
    pauseOverlay.className = "hover-idle-mp-slot-pause-overlay";
    pauseOverlay.setAttribute("aria-hidden", "true");
    pauseOverlay.innerHTML = pauseIconSource;

    const blockOverlay = document.createElement("span");
    blockOverlay.className = "hover-idle-mp-slot-block-overlay";
    blockOverlay.setAttribute("aria-hidden", "true");
    blockOverlay.innerHTML = banIconSource;

    element.append(fill, icon, pauseOverlay, blockOverlay);

    const durationGetter = this.getHoverToggleDurationMs;
    const controller = new MpSlotChargeController({
      element,
      pointerModel: this.pointerModel,
      getChargeMs: durationGetter ? () => durationGetter() : undefined,
      onAssign: () =>
        scope === "wall"
          ? this.onMpWallAssign?.(target)
          : this.onHovGainUpgradeMpAssign?.(target),
      onRemove: () =>
        scope === "wall"
          ? this.onMpWallRemove?.(target)
          : this.onHovGainUpgradeMpRemove?.(target),
    });
    return { element, controller, target };
  }

  private beginPulseBurst(): void {
    this.pulseBurstDepth += 1;
  }

  private endPulseBurst(): void {
    if (this.pulseBurstDepth <= 0) {
      return;
    }
    this.pulseBurstDepth -= 1;
    if (this.pulseBurstDepth === 0) {
      this.flushPendingPulseRequests();
    }
  }

  private queuePulseRequest(
    button: HTMLButtonElement,
    request: PendingPulseRequest,
  ): void {
    if (this.pulseBurstDepth === 0) {
      this.applyPulseRequest(button, request);
      return;
    }
    const current = this.pendingPulseRequests.get(button);
    this.pendingPulseRequests.set(
      button,
      mergePendingPulseRequest(current, request),
    );
  }

  private flushPendingPulseRequests(): void {
    const entries = [...this.pendingPulseRequests.entries()];
    this.pendingPulseRequests.clear();
    for (const [button, request] of entries) {
      this.applyPulseRequest(button, request);
    }
  }

  private applyPulseRequest(
    button: HTMLButtonElement,
    request: PendingPulseRequest,
  ): void {
    if (request.kind === "milestone") {
      this.triggerMilestonePulse(button);
    } else if (request.kind === "normal") {
      this.triggerPulse(button, request.pulseSeconds);
    } else {
      this.clearAllPulses(button);
    }
  }

  private triggerPulse(button: HTMLButtonElement, pulseSeconds: number): void {
    this.clearAllPulses(button);
    button.style.setProperty(
      "--upgrade-pulse-duration",
      `${pulseSeconds.toFixed(3)}s`,
    );
    void button.offsetWidth;
    button.dataset.pulse = "true";

    const timeoutMs = Math.max(0, pulseSeconds * 1000);
    const timeoutId = window.setTimeout(() => {
      this.clearPulse(button);
    }, timeoutMs);
    this.pulseClearTimeouts.set(button, timeoutId);
  }

  /**
   * Fires the dedicated milestone purchase pulse. It replaces any in-flight
   * normal pulse so the two never share the animated `box-shadow`, and clears
   * itself after {@link MILESTONE_PULSE_DURATION_MS}.
   */
  private triggerMilestonePulse(button: HTMLButtonElement): void {
    this.clearAllPulses(button);
    void button.offsetWidth;
    button.dataset.milestonePulse = "true";

    const timeoutId = window.setTimeout(() => {
      this.clearMilestonePulse(button);
    }, MILESTONE_PULSE_DURATION_MS);
    this.milestonePulseClearTimeouts.set(button, timeoutId);
  }

  /**
   * Marks (or clears) the subtle milestone-ready affordance on a row. The state
   * is mirrored onto both the button and the resting card so the cue shows in
   * the hover and at-rest views without affecting other rows.
   */
  private setMilestoneReady(
    row: GroupingHovGainUpgradeRowNodes,
    ready: boolean,
  ): void {
    row.isMilestoneReady = ready;
    if (ready) {
      row.button.dataset.milestoneReady = "true";
      row.card.dataset.milestoneReady = "true";
    } else {
      delete row.button.dataset.milestoneReady;
      delete row.card.dataset.milestoneReady;
    }
  }

  private clearPulse(button: HTMLButtonElement): void {
    const timeoutId = this.pulseClearTimeouts.get(button);
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId);
      this.pulseClearTimeouts.delete(button);
    }
    delete button.dataset.pulse;
  }

  private clearMilestonePulse(button: HTMLButtonElement): void {
    const timeoutId = this.milestonePulseClearTimeouts.get(button);
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId);
      this.milestonePulseClearTimeouts.delete(button);
    }
    delete button.dataset.milestonePulse;
  }

  /** Clears both the normal and milestone purchase pulse on a button. */
  private clearAllPulses(button: HTMLButtonElement): void {
    this.pendingPulseRequests.delete(button);
    this.clearPulse(button);
    this.clearMilestonePulse(button);
  }

  pulseEntry(
    target: HovGainUpgradeMpTarget,
    options: { readonly isMilestone?: boolean } = {},
  ): void {
    if (!this.getMotionPolicy().upgradePurchasePulse) {
      return;
    }
    const row = this.findRowForTarget(target);
    if (row === null) {
      return;
    }
    if (options.isMilestone === true) {
      this.triggerMilestonePulse(row.button);
    } else {
      this.triggerPulse(row.button, this.getInitialIntervalSeconds());
    }
  }

  private findRowForTarget(
    target: HovGainUpgradeMpTarget,
  ): GroupingHovGainUpgradeRowNodes | null {
    if (target === "global") {
      return this.rows.find((r) => r.entry.kind === "global") ?? null;
    }
    return (
      this.rows.find(
        (r) => r.entry.kind === "cell" && r.entry.index === target.index,
      ) ?? null
    );
  }

  private getInitialIntervalSeconds(): number {
    if (this.initialIntervalMs === undefined) {
      return DEFAULT_UPGRADE_REPEAT_INITIAL_INTERVAL_MS / 1000;
    }
    const ms =
      typeof this.initialIntervalMs === "function"
        ? this.initialIntervalMs()
        : this.initialIntervalMs;
    return ms / 1000;
  }

  private hideAllTooltips(): void {
    for (const row of this.rows) {
      row.interaction.hideTooltip();
    }
  }
}
